import * as i0 from '@angular/core';
import { EventEmitter, Injectable, Directive, HostListener, NgModule } from '@angular/core';
import { BehaviorSubject, Subject, of, EMPTY } from 'rxjs';
import { map, tap, debounceTime, switchMap, catchError, mergeMap } from 'rxjs/operators';
import { untilDestroyed } from '@orchestrator/ngx-until-destroyed';
import { HttpParams } from '@angular/common/http';
import * as i1 from 'ngx-webstorage';
import { NgxWebstorageModule } from 'ngx-webstorage';
import * as i1$1 from '@angular/router';
import * as i3 from '@angular/forms';
import * as i4 from '@ngx-translate/core';
import * as i5 from '@angular/common';
import * as i6 from '@ng-bootstrap/ng-bootstrap';

var EventType;
(function (EventType) {
    EventType[EventType["CREATE"] = 0] = "CREATE";
    EventType[EventType["DELETE"] = 1] = "DELETE";
    EventType[EventType["UPDATE"] = 2] = "UPDATE";
    EventType[EventType["LOAD"] = 3] = "LOAD";
    EventType[EventType["LOADING"] = 4] = "LOADING";
    EventType[EventType["RELOAD"] = 5] = "RELOAD";
})(EventType || (EventType = {}));

var CRUDMode;
(function (CRUDMode) {
    CRUDMode[CRUDMode["CREATE"] = 0] = "CREATE";
    CRUDMode[CRUDMode["READ"] = 1] = "READ";
    CRUDMode[CRUDMode["UPDATE"] = 2] = "UPDATE";
    CRUDMode[CRUDMode["DELETE"] = 3] = "DELETE";
})(CRUDMode || (CRUDMode = {}));

class PaginationState {
}

class PageWrapper {
}

class Sort {
}

class Event {
    constructor(resourceName, type, resource) {
        this.resourceName = resourceName;
        this.type = type;
        this.resource = resource;
        this.toString = () => {
            return `Event ( resourceName: ${this.resourceName} type: ${EventType[this.type]} )`;
        };
    }
    getType() { return this.type; }
    getName() {
        return this.resourceName + ':' + this.type;
    }
    getResource() {
        return this.resource;
    }
}

class NVCrudService {
    constructor(http, baseURI, resourceName, nvEventService, auditProperties) {
        this.internalAuditProperties = [];
        this.internalHttp = http;
        this.internalBaseURI = baseURI;
        this.internalResourceName = resourceName;
        this.internalNVEventService = nvEventService;
        if (auditProperties) {
            this.internalAuditProperties = auditProperties;
        }
    }
    bindEvent(type, listener) {
        return this.internalNVEventService.bind(new Event(this.getResourceName(), type), listener);
    }
    getResourceName() {
        return this.internalResourceName;
    }
    getBaseURI() {
        return this.internalBaseURI;
    }
    getParentResourceURI(pathVariables) {
        return this.getBaseURI();
    }
    getResourcesURI(pathVariables) {
        return this.getParentResourceURI(pathVariables) + this.internalResourceName + '/';
    }
    getResourceURI(pathVariables, resourceID) {
        return this.getResourcesURI(pathVariables) + `${resourceID}/`;
    }
    internalFindAll(pathVariables, searchTerm = '', terms = [], sortColumn = 'id', sortDirection = 'asc', page = 0, pageSize = 1, filter = null, isPaged) {
        let params = new HttpParams()
            .set('isPaged', isPaged.toString());
        if (page) {
            params = params.append('page', page.toString());
        }
        if (pageSize) {
            params = params.append('size', pageSize.toString());
        }
        if (sortColumn && sortColumn !== '') {
            params = params.append('sort', sortColumn + ',' + sortDirection);
        }
        if (searchTerm && searchTerm !== '') {
            params = params.append('searchTerm', searchTerm);
            terms.forEach(term => {
                params = params.append('terms', term);
            });
        }
        if (filter) {
            filter.forEach((value, key) => {
                if (typeof value === 'string') {
                    params = params.append(key, value);
                }
                else {
                    value.forEach(item => {
                        params = params.append(key, item);
                    });
                }
            });
        }
        this.internalNVEventService.emit(new Event(this.getResourceName(), EventType.LOADING));
        return this.internalHttp
            .get(this.getResourcesURI(pathVariables), {
            params,
            observe: 'response'
        })
            .pipe(map(res => res.body))
            .pipe(map(res => {
            this.internalNVEventService.emit(new Event(this.getResourceName(), EventType.LOAD, res));
            return res;
        }));
    }
    // get the list of elements of an entity
    findAll(pathVariables, searchTerm = '', terms = [], sortColumn = '', sortDirection = 'asc', page = 0, pageSize = 1, filter = null) {
        return this.internalFindAll(pathVariables, searchTerm, terms, sortColumn, sortDirection, page, pageSize, filter, true);
    }
    // get a list of items of the entity
    listAll(pathVariables, searchTerm = '', terms = [], sortColumn = '', sortDirection = 'asc', filter = null) {
        return this.internalFindAll(pathVariables, searchTerm, terms, sortColumn, sortDirection, null, null, filter, false);
    }
    // create an element of an entity
    create(pathVariables, resource, onlyValidation = false) {
        const extraParams = new HttpParams()
            .set('onlyValidation', onlyValidation.toString());
        return this.internalHttp
            .post(this.getResourcesURI(pathVariables), this.removeAuditValues(resource), {
            responseType: 'text',
            params: extraParams
        })
            .pipe(map(res => {
            this.internalNVEventService.emit(new Event(this.getResourceName(), EventType.CREATE, res));
            return res;
        }));
    }
    // get an element of an entity by a given id
    findOne(pathVariables, resourceID) {
        return this.internalHttp
            .get(this.getResourceURI(pathVariables, resourceID), {
            params: new HttpParams(),
            observe: 'response'
        }).pipe(map(res => res.body))
            .pipe(map(res => {
            this.internalNVEventService.emit(new Event(this.getResourceName(), EventType.LOAD, res));
            return res;
        }));
    }
    // update an element of an entity by a given id
    update(pathVariables, resourceID, resource, onlyValidation = false) {
        const extraParams = new HttpParams()
            .set('onlyValidation', onlyValidation.toString());
        return this.internalHttp
            .put(this.getResourceURI(pathVariables, resourceID), this.removeAuditValues(resource), { params: extraParams })
            .pipe(map(res => {
            this.internalNVEventService.emit(new Event(this.getResourceName(), EventType.UPDATE, resource));
            return res;
        }));
    }
    // delete an element of an entity by a given id
    delete(pathVariables, resourceID) {
        return this.internalHttp
            .delete(this.getResourceURI(pathVariables, resourceID))
            .pipe(map(res => {
            this.internalNVEventService.emit(new Event(this.getResourceName(), EventType.DELETE, resourceID));
            return res;
        }));
    }
    removeAuditValues(resource) {
        this.internalAuditProperties.forEach(property => {
            if (resource.hasOwnProperty(property)) {
                delete resource[property];
            }
        });
        return resource;
    }
}

class NVEventService {
    constructor() {
        this.eventEmitter = new EventEmitter();
    }
    bind(targetEvent, listener) {
        return this.eventEmitter.subscribe((event) => {
            if (event.getName() === targetEvent.getName()) {
                listener(event);
            }
        });
    }
    emit(event) {
        this.eventEmitter.emit(event);
    }
}
/** @nocollapse */ NVEventService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVEventService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ NVEventService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVEventService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVEventService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class NVPreferencesService {
    constructor(internalSessionStorageService) {
        this.internalSessionStorageService = internalSessionStorageService;
    }
    savePreference(name, value) {
        if (name !== null) {
            this.internalSessionStorageService.store(name.toUpperCase(), value);
        }
    }
    readPreference(name, defaultValue) {
        if (name !== null && this.internalSessionStorageService.retrieve(name.toUpperCase()) !== null) {
            return this.internalSessionStorageService.retrieve(name.toUpperCase());
        }
        else {
            return defaultValue;
        }
    }
}
/** @nocollapse */ NVPreferencesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVPreferencesService, deps: [{ token: i1.SessionStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ NVPreferencesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVPreferencesService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVPreferencesService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.SessionStorageService }]; } });

class NVNotificationService {
    constructor() {
        this.notifications = [];
    }
    show(textOrTpl, options = {}) {
        this.notifications.push(Object.assign({ textOrTpl }, options));
    }
    showSuccess(textOrTpl, options = {}) {
        this.show(textOrTpl, { classname: 'bg-success text-light', delay: 3000 });
    }
    showError(textOrTpl) {
        this.show(textOrTpl, { classname: 'bg-danger text-light', delay: 3000 });
    }
    showWarning(textOrTpl) {
        this.show(textOrTpl, { classname: 'bg-warning', delay: 3000 });
    }
    remove(notification) {
        this.notifications = this.notifications.filter(t => t !== notification);
    }
}
/** @nocollapse */ NVNotificationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVNotificationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ NVNotificationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVNotificationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVNotificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class NVUtilsService {
    static findMissingProperties(base, compared, deepSearchFlag) {
        const missing = [];
        const compareProp = (baseValue, comparedValue, path, deepSearch) => {
            if (baseValue !== null && comparedValue === undefined) {
                missing.push(path.join('.'));
                if (!deepSearch) {
                    return missing;
                }
            }
            if (baseValue !== null && typeof baseValue === 'object') {
                Object.keys(baseValue).forEach((key) => {
                    compareProp(baseValue[key], comparedValue && comparedValue[key], path.concat(key), deepSearch);
                });
            }
        };
        Object.keys(base).forEach((key) => {
            compareProp(base[key], compared[key], [key], deepSearchFlag);
        });
        return missing;
    }
    static loadPathVariables(params) {
        const pathVariables = new Map();
        // collect localRoute params to use in service
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                pathVariables.set(key, params[key]);
            }
        }
        return pathVariables;
    }
    static compareValues(datePipe, a, b) {
        // Create arrays of property names
        const aProps = Object.getOwnPropertyNames(a);
        const bProps = Object.getOwnPropertyNames(b);
        // If number of properties is different, objects are not equivalent
        if (aProps.length !== bProps.length) {
            return false;
        }
        for (const propName of aProps) {
            // If values of same property are not equal, objects are not equivalent
            if (a[propName] !== b[propName]) {
                if (b[propName] !== null && typeof b[propName].getMonth === 'function') {
                    if (!isNaN(+new Date(b[propName])) && !isNaN(+new Date(a[propName]))) {
                        // different check for the date
                        if (datePipe.transform(a[propName], 'shortDate') !== datePipe.transform(b[propName], 'shortDate')) {
                            return false;
                        }
                    }
                }
                else {
                    return false;
                }
            }
        }
        // If we made it this far, objects are considered equivalent
        return true;
    }
}

class NVListController {
    constructor(internalCrudService, internalRoute, internalNVPreferencesService, defaultPageSizeOptions, defaultPageSize, terms) {
        this.internalCrudService = internalCrudService;
        this.internalRoute = internalRoute;
        this.internalNVPreferencesService = internalNVPreferencesService;
        this.defaultPageSizeOptions = defaultPageSizeOptions;
        this.defaultPageSize = defaultPageSize;
        this.terms = terms;
        this.subscriptions = new Set();
        this.loading$ = new BehaviorSubject(true);
        this.search$ = new Subject();
        this.items$ = new BehaviorSubject([]);
        this.total$ = new BehaviorSubject(0);
        this.state$ = new PaginationState();
        this.pathVariables$ = new Map(); // store the route params
        this.state$ = {
            page: 1,
            pageSize: this.defaultPageSize,
            searchTerm: '',
            sortColumn: '',
            sortDirection: '',
            filter: new Map()
        };
        this.pageSize =
            this.internalNVPreferencesService.readPreference(this.internalCrudService.getResourceName() + '_NV_PAGESIZE', this.defaultPageSize);
    }
    search() {
        this.internalNVPreferencesService.savePreference(this.internalCrudService.getResourceName() + '_NV_PAGESIZE', this.state$.pageSize);
        return this.internalCrudService.findAll(this.pathVariables$, this.state$.searchTerm, this.terms, this.state$.sortColumn, this.state$.sortDirection, this.state$.page - 1, this.state$.pageSize, this.state$.filter);
    }
    ngOnInit() {
        this.subscriptions.add(this.internalRoute.params
            .pipe(untilDestroyed(this))
            .subscribe(params => {
            this.pathVariables$ = NVUtilsService.loadPathVariables(params);
            this.subscriptions.add(this.internalCrudService.bindEvent(EventType.LOAD, (event) => { this.onLoad(event.getResource()); }));
            this.subscriptions.add(this.internalCrudService.bindEvent(EventType.LOADING, (event) => { this.onLoading(); }));
            this.subscriptions.add(this.internalCrudService.bindEvent(EventType.DELETE, (event) => { this.onDelete(event.getResource()); }));
            this.subscriptions.add(this.search$.pipe(tap(() => this.loading$.next(true)), debounceTime(200), switchMap(() => this.search()), tap(() => this.loading$.next(false))).subscribe(result => {
                this.items$.next(result.content);
                this.total$.next(result.totalElements);
            }));
            this.search$.next();
        }));
    }
    addSubscription(subscription) {
        this.subscriptions.add(subscription);
    }
    sortBy(column) {
        if (this.sortColumn !== column) {
            this.sortColumn = column;
            this.sortDirection = 'desc';
        }
        else {
            this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
        }
    }
    isSortedBy(column, direction) {
        if (this.sortColumn === column) {
            return this.sortDirection === direction;
        }
        else {
            return false;
        }
    }
    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }
    // Getters and Setters
    get items() { return this.items$.asObservable(); }
    get total() { return this.total$.asObservable(); }
    get loading() { return this.loading$.asObservable(); }
    get pathVariables() { return this.pathVariables$; }
    set pathVariables(newPV) { this.pathVariables$ = newPV; }
    get filter() { return this.state$.filter; }
    set filter(filter) { this._set({ filter }); }
    get page() { return this.state$.page; }
    set page(page) { this._set({ page }); }
    get pageSize() { return this.state$.pageSize; }
    set pageSize(pageSize) { this._set({ pageSize }); }
    get searchTerm() { return this.state$.searchTerm; }
    set searchTerm(searchTerm) { this._set({ searchTerm }); }
    get sortColumn() { return this.state$.sortColumn; }
    set sortColumn(sortColumn) { this._set({ sortColumn }); }
    get sortDirection() { return this.state$.sortDirection; }
    set sortDirection(sortDirection) { this._set({ sortDirection }); }
    get pageSizeOptions() { return this.defaultPageSizeOptions; }
    _set(patch) {
        Object.assign(this.state$, patch);
        this.search$.next();
    }
    // Abstract methods called on event
    onLoad(resource) { }
    onLoading() { }
    onCreate(id) { }
    onDelete(id) { }
    onUpdate(resource) { }
}
/** @nocollapse */ NVListController.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVListController, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ NVListController.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.0", type: NVListController, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVListController, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: NVCrudService }, { type: i1$1.ActivatedRoute }, { type: NVPreferencesService }, { type: undefined }, { type: undefined }, { type: undefined }]; } });

class NVFormController {
    constructor(internalRoute, internalResourceType, internalResourceIDPropertyName, internalCrudService, internalFormBuilder, internalNotificationService, internalRouter, internalTranslateService, internalDatePipe, resourceID, validationOnChangeEnabled, modal) {
        this.internalRoute = internalRoute;
        this.internalResourceType = internalResourceType;
        this.internalResourceIDPropertyName = internalResourceIDPropertyName;
        this.internalCrudService = internalCrudService;
        this.internalFormBuilder = internalFormBuilder;
        this.internalNotificationService = internalNotificationService;
        this.internalRouter = internalRouter;
        this.internalTranslateService = internalTranslateService;
        this.internalDatePipe = internalDatePipe;
        this.resourceID = resourceID;
        this.validationOnChangeEnabled = validationOnChangeEnabled;
        this.modal = modal;
        this.subscriptions = new Set();
        this.submitted = false;
        this.crudModeType = CRUDMode;
        this.crudMode = CRUDMode.CREATE;
        this.pathVariables$ = new Map();
        this.resource = this.createNewResourceObject();
        this.resourceForm = this.internalFormBuilder.group(this.resource);
    }
    ngOnInit() {
        if (this.validationOnChangeEnabled) {
            this.subscriptions.add(this.resourceForm.valueChanges.subscribe(() => {
                this.onSubmit(true);
            }));
        }
        this.subscriptions.add(this.internalRoute.params
            .pipe(untilDestroyed(this))
            .subscribe(params => {
            this.pathVariables$ = NVUtilsService.loadPathVariables(params);
            // call a before init hook for extended class
            this.subscriptions.add(this.onBeforeInit()
                .subscribe((beforeInitEvent) => {
                this.subscriptions.add(this.internalRoute.data
                    .pipe(untilDestroyed(this))
                    .subscribe(routeData => {
                    // load the resource from route data
                    const routingResource = routeData[this.internalCrudService.getResourceName()];
                    // get the CRUD mode (if specified)
                    if (routeData.crudMode) {
                        this.crudMode = routeData.crudMode;
                    }
                    // the compare function from the UtilsService verifies that the object passed by the router
                    // has the same set of properties than the resource class expected
                    if (routingResource &&
                        NVUtilsService.findMissingProperties(this.getResource(), routingResource, true).length === 0) {
                        this.setResource(routingResource);
                        this.subscriptions.add(this.onAfterInit()
                            .subscribe((afterInitEvent) => {
                        }));
                    }
                    else if (this.resourceID
                        && (this.crudMode === CRUDMode.READ
                            || this.crudMode === CRUDMode.UPDATE)) {
                        // get the resource using services
                        this.subscriptions.add(this.internalCrudService
                            .findOne(this.pathVariables$, this.resourceID)
                            .pipe(untilDestroyed(this))
                            .subscribe(resource => {
                            this.setResource(resource);
                            console.log(resource);
                            this.subscriptions.add(this.onAfterInit()
                                .subscribe((afterInitEvent) => {
                            }));
                        }));
                    }
                }));
            }));
        }));
    }
    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }
    addOptionalMenu() { }
    // since the form builder takes the resource class as parameter, it is needed to add optionals validators here
    addFormFieldsValidators() { }
    onBeforeInit() {
        return of(null);
    }
    onSubmitRedirect(crudMode) {
        if (crudMode === CRUDMode.CREATE) {
            this.internalRouter.navigate(['../'], { relativeTo: this.internalRoute });
        }
        else if (crudMode === CRUDMode.UPDATE) {
            this.internalRouter.navigate(['../../'], { relativeTo: this.internalRoute });
        }
    }
    onAfterInit() {
        return of(null);
    }
    getControlErrors(field) {
        return Object.keys(this.f[field].errors);
    }
    validationError(errorResponse, isValidation) {
        if (errorResponse.status === 400) {
            const error = JSON.parse(errorResponse.error);
            if (error.errorParams && error.errorParams.validationErrors) {
                const fields = Object.keys(error.errorParams.validationErrors);
                for (const field of fields) {
                    const errors = {};
                    for (const i of error.errorParams.validationErrors[field]) {
                        errors[field + '.' + i.code] = true;
                    }
                    if (!isValidation || (this.f[field].dirty || this.f[field].touched)) {
                        this.f[field].setErrors(errors);
                        this.f[field].markAsDirty();
                    }
                }
            }
        }
    }
    onSubmit(onlyValidation = false, isModal = false) {
        const that = this;
        // stop here if form is invalid
        if (!onlyValidation && (that.resourceForm.invalid || that.crudMode === CRUDMode.READ)) {
            return;
        }
        that.submitted = true; // this has to be set to true in order that the deactivate guard let the localRoute changes
        // create a new entity based on the form data
        const formDataEntity = that.createNewResourceObject(that.resourceForm.getRawValue());
        // if the form based entity is different than the entity, save the entity
        if (!NVUtilsService.compareValues(that.internalDatePipe, that.getResource(), formDataEntity)) {
            if (that.crudMode === CRUDMode.CREATE) {
                // it is a new entity, create it
                that.internalCrudService.create(that.pathVariables$, formDataEntity, onlyValidation)
                    .subscribe(id => {
                    if (!onlyValidation) {
                        that.internalNotificationService.showSuccess(that.internalTranslateService.instant('resource.' + that.resourceNameLabel + '.modal.create.successMessage', { resourceId: id }));
                        if (isModal) {
                            this.modal.close(id);
                        }
                        else {
                            this.onSubmitRedirect(that.crudMode);
                        }
                    }
                }, errorResponse => { that.validationError(errorResponse, onlyValidation); });
            }
            else {
                // it is an existing entity, update it
                that.internalCrudService.update(that.pathVariables$, that.resourceID, formDataEntity, onlyValidation).subscribe(() => {
                    if (!onlyValidation) {
                        that.internalNotificationService.showSuccess(that.internalTranslateService.instant('resource.' + that.resourceNameLabel + '.modal.update.successMessage', { resourceId: that.resourceID }));
                        if (isModal) {
                            this.modal.close();
                        }
                        else {
                            this.onSubmitRedirect(that.crudMode);
                        }
                    }
                }, errorResponse => { that.validationError(errorResponse, onlyValidation); });
            }
        }
        else { // otherwise, go back to the list of entities without saving any changes
            if (!onlyValidation) {
                that.internalNotificationService.showWarning(that.internalTranslateService.instant('resource.' + that.resourceNameLabel + '.modal.update.nothingChanged', { resourceId: that.resourceID }));
                if (isModal) {
                    this.modal.close();
                }
                else {
                    this.onSubmitRedirect(that.crudMode);
                }
            }
        }
    }
    setResource(resource) {
        this.resource = this.createNewResourceObject(resource);
        this.resourceID = this.resource[this.internalResourceIDPropertyName];
        // update the form
        this.resourceForm.patchValue(this.resource);
        // add fields validators
        this.addFormFieldsValidators();
        // add the optional menu (such as edit item)
        this.addOptionalMenu();
    }
    getResource() {
        return this.resource;
    }
    // convenience getter for resourceNameLabel
    get resourceNameLabel() {
        return this.internalCrudService.getResourceName().toLowerCase();
    }
    // convenience getter for easy access to form fields
    get f() { return this.resourceForm.controls; }
    // this function prevent the user to close the browser tab if there are some unsaved changes
    onWindowClose(event) {
        const formDataEntity = this.createNewResourceObject(this.resourceForm.getRawValue());
        if (!this.submitted && !NVUtilsService.compareValues(this.internalDatePipe, this.getResource(), formDataEntity)) {
            event.preventDefault();
            event.returnValue = false;
        }
    }
    // this is the method that the guard calls to see if the component can be deactivated
    canDeactivate() {
        const formDataEntity = this.createNewResourceObject(this.resourceForm.getRawValue());
        if (!this.submitted && !NVUtilsService.compareValues(this.internalDatePipe, this.getResource(), formDataEntity)) {
            return confirm(this.internalTranslateService.instant('deactivate.unsavedChanges'));
        }
        return true;
    }
    // this function restore the form at its original state and resource values
    resetFormWithOriginalResourceData() {
        if (this.crudMode === CRUDMode.READ) {
            return;
        }
        this.resourceForm.patchValue(this.getResource());
        this.resourceForm.markAsPristine();
        this.resourceForm.markAsUntouched();
    }
    get pathVariables() { return this.pathVariables$; }
    set pathVariables(newPV) { this.pathVariables$ = newPV; }
    createNewResourceObject(obj) {
        return obj != null ? new this.internalResourceType(obj) : new this.internalResourceType();
    }
}
/** @nocollapse */ NVFormController.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVFormController, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ NVFormController.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.0", type: NVFormController, host: { listeners: { "window:beforeunload": "onWindowClose($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVFormController, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i1$1.ActivatedRoute }, { type: undefined }, { type: undefined }, { type: NVCrudService }, { type: i3.FormBuilder }, { type: NVNotificationService }, { type: i1$1.Router }, { type: i4.TranslateService }, { type: i5.DatePipe }, { type: undefined }, { type: undefined }, { type: i6.NgbActiveModal }]; }, propDecorators: { onWindowClose: [{
                type: HostListener,
                args: ['window:beforeunload', ['$event']]
            }] } });

class CanDeactivateGuard {
    canDeactivate(component) {
        return component.canDeactivate && component.canDeactivate();
    }
}
/** @nocollapse */ CanDeactivateGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: CanDeactivateGuard, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ CanDeactivateGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: CanDeactivateGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: CanDeactivateGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class NVPathVariableResourceResolver {
    constructor(internalCrudService, internalRouter, internalRoute) {
        this.internalCrudService = internalCrudService;
        this.internalRouter = internalRouter;
        this.internalRoute = internalRoute;
    }
    resolve(internalRoute) {
        const pathVariables = NVUtilsService.loadPathVariables(internalRoute.params);
        const resourceID = pathVariables.get(this.internalCrudService.getResourceName() + 'ID');
        if (resourceID) {
            return this.internalCrudService
                .findOne(pathVariables, resourceID)
                .pipe(catchError(error => {
                console.error(this.constructor.name, error);
                this.internalRouter.navigate(['../../'], { relativeTo: this.internalRoute });
                return EMPTY;
            }), mergeMap(resource => {
                return resource ? of(resource) : EMPTY;
            }));
        }
        else {
            return EMPTY;
        }
    }
}

class NVCoreModule {
    static forRoot() {
        return {
            ngModule: NVCoreModule,
            providers: [
            // SessionStorageService,
            ]
        };
    }
}
/** @nocollapse */ NVCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ NVCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVCoreModule, imports: [NgxWebstorageModule] });
/** @nocollapse */ NVCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVCoreModule, providers: [
        NVEventService,
        NVNotificationService,
        NVPreferencesService
    ], imports: [[
            NgxWebstorageModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: NVCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        NgxWebstorageModule,
                    ],
                    providers: [
                        NVEventService,
                        NVNotificationService,
                        NVPreferencesService
                    ],
                }]
        }] });

/*
 * Public API Surface of nv-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CRUDMode, CanDeactivateGuard, Event, EventType, NVCoreModule, NVCrudService, NVEventService, NVFormController, NVListController, NVNotificationService, NVPathVariableResourceResolver, NVPreferencesService, NVUtilsService, PageWrapper, Sort };
